// Final Enhanced ChatbotPage.tsx with initial greeting storage
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../helpers/Supabase';
import '../../../styles/components/ChatbotPage.scss';
import Loader from '../../Loader/Loader';
import ChatbotLoader from '../../Loader/ChatbotLoader';
const ChatbotPage: React.FC = () => {
  const { hash_id } = useParams<{ hash_id: string }>();
  const [config, setConfig] = useState<any | null>(null);
  const [storeDetails, setStoreDetails] = useState<any | null>(null);
  const [surveyId, setSurveyId] = useState<string | null>(null);
  const [convoHistory, setConvoHistory] = useState<{ role: string; content: string }[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [started, setStarted] = useState(false);
  const [, setShowTipJar] = useState(false);
  const [isTipJarMinimized, setIsTipJarMinimized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isKioskMode, setIsKioskMode] = useState(false);
  const tipJarTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check for kiosk mode from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const kiosk = urlParams.get('kiosk');
    
    setIsKioskMode(kiosk === 'true');

    // Hide navbar and other UI elements in kiosk mode
    if (kiosk === 'true') {
      const navbar = document.querySelector('.navbar') as HTMLElement;
      const sidenav = document.querySelector('.sidenav') as HTMLElement;
      const mainContent = document.querySelector('.main-content') as HTMLElement;
      
      if (navbar) navbar.style.display = 'none';
      if (sidenav) sidenav.style.display = 'none';
      if (mainContent) mainContent.style.marginLeft = '0';
      
      // Hide browser UI elements
      document.body.style.overflow = 'hidden';
    }

    const fetchConfig = async () => {
      const { data: storeData, error: storeErr } = await supabase
        .from('stores')
        .select('id, store_name, design_config, client_id')
        .eq('hash_id', hash_id)
        .maybeSingle();

      if (!storeData || storeErr) {
        console.error('Failed to load store config', storeErr);
        return;
      }

      const { data: clientData } = await supabase
        .from('clients')
        .select('name, logo_url')
        .eq('id', storeData.client_id)
        .maybeSingle();

      const { data: flowData, error: flowErr } = await supabase
        .from('chatbot_flows')
        .select('*')
        .eq('flow_type', 'survey')
        .eq('store_id', storeData.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (flowErr) console.error('Error loading chatbot flow for store:', flowErr);

      setStoreDetails({
        store_id: storeData.id,
        client_id: storeData.client_id,
        store_name: storeData?.store_name || clientData?.name,
        logo_url: clientData?.logo_url,
        design_config: storeData?.design_config
      });

      setConfig({
        ...flowData,
        gpt_instructions: flowData?.gpt_instructions
      });

      setTimeout(() => setIsLoading(false), 2300);
    };

    fetchConfig();
  }, [hash_id]);

  useEffect(() => {
    if (config && storeDetails && !started) {
      const initialGreeting = config.greeting || "Hey there! 👋 Who did you work with today?";
      addMessage(initialGreeting, 'bot');

      fetch('https://web-production-79b0f.up.railway.app/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [],
          analyze_sentiment: false,
          store_id: storeDetails.store_id,
          client_id: storeDetails.client_id,
          store_name: storeDetails.store_name,
          customer_name: 'Customer',
          gpt_instructions: config.gpt_instructions,
          custom_greeting: initialGreeting,
          survey_id: surveyId
        }),
      })
        .then(res => res.json())
        .then(data => {
          if (data?.survey_id) setSurveyId(data.survey_id);
        })
        .catch(err => console.error("Greeting send failed:", err));

      setStarted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when config/storeDetails ready
  }, [config, storeDetails]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [convoHistory, isTyping]);

  useEffect(() => {
    return () => {
      if (tipJarTimeoutRef.current) clearTimeout(tipJarTimeoutRef.current);
    };
  }, []);

  const addMessage = (content: string, role: string) => {
    const mappedRole = role === 'bot' ? 'assistant' : role;
    setConvoHistory((prev) => [...prev, { role: mappedRole, content }]);
  };

  const getBotReply = async (userMessage: string) => {
    setIsTyping(true);

    const updatedHistory = userMessage
      ? [...convoHistory, { role: 'user', content: userMessage }]
      : convoHistory;

    if (userMessage) addMessage(userMessage, 'user');

    try {
      const response = await fetch('https://web-production-79b0f.up.railway.app/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: updatedHistory.slice(-8),
          analyze_sentiment: true,
          store_id: storeDetails.store_id,
          client_id: storeDetails.client_id,
          store_name: storeDetails.store_name,
          customer_name: 'Customer',
          gpt_instructions: config.gpt_instructions,
          survey_id: surveyId
        }),
      });

      const data = await response.json();
      if (data?.reply) {
        addMessage(data.reply, 'bot');
        if (!surveyId && data.survey_id) setSurveyId(data.survey_id);
        if (data.sentiment === 'positive' && !isTipJarMinimized) {
          setShowTipJar(true);
          if (tipJarTimeoutRef.current) clearTimeout(tipJarTimeoutRef.current);
          tipJarTimeoutRef.current = setTimeout(() => setIsTipJarMinimized(true), 7000);
        }
      } else {
        addMessage("Hmm, I didn’t catch that. Mind rephrasing?", 'bot');
      }
    } catch (err) {
      console.error("GPT fetch error:", err);
      addMessage("Oops! Something went wrong. Wanna try that again?", 'bot');
    }

    setIsTyping(false);
  };

  const handleChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    getBotReply(inputValue);
    setInputValue('');
  };

  if (isLoading) return <ChatbotLoader />;
  if (!config || !storeDetails) return <Loader />;

  const overlayStyle = storeDetails.design_config?.overlayMode === 'solid'
    ? { background: storeDetails.design_config.solidColor }
    : { background: `linear-gradient(to right, ${storeDetails.design_config?.gradientStart}, ${storeDetails.design_config?.gradientEnd})` };

  return (
    <div className={`chatbot-page ${isKioskMode ? 'kiosk-mode' : ''}`}>
      <div className="background">
        {storeDetails.design_config?.bgType === 'video' ? (
          <video autoPlay loop muted className="background-video">
            <source src={storeDetails.design_config.bgUrl} type="video/mp4" />
          </video>
        ) : (
          <img src={storeDetails.design_config?.bgUrl} alt="Background" className="background-image" />
        )}
        <div className="overlay" style={overlayStyle} />
      </div>

      <div className="chat-container">
        <div className="store-header">
          {storeDetails.logo_url && <img src={storeDetails.logo_url} alt="Store Logo" className="store-logo" />}
          {storeDetails.store_name && <h2 className="store-name">{storeDetails.store_name}</h2>}
        </div>

        <div className="chat-box">
          <div className="messages">
            {convoHistory.map((msg, i) => (
              <div key={i} className={`message ${msg.role}`}>{msg.content}</div>
            ))}
            {isTyping && (
              <div className="message bot typing-indicator">
                <span className="dot" /><span className="dot" /><span className="dot" />
              </div>
            )}
            {/* {showTipJar && !isTipJarMinimized && (
              <div className="message bot tipjar-message">
                <TipJar storeName={storeDetails.store_name || 'Store'} stripeUrl="https://stripe.com/tip" />
                <button className="minimize-button" onClick={() => setIsTipJarMinimized(true)}>Minimize</button>
              </div>
            )} */}
            {isTipJarMinimized && (
              <div className="tipjar-widget" onClick={() => setIsTipJarMinimized(false)}>
                <span className="widget-icon">💰</span><span>Tip Us!</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleChatSubmit} className="chat-input">
            <input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
            />
            <button type="submit">Send</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatbotPage;
